import axios from 'axios';
import { baseAPI } from '../config';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

/**
 * Get emails for an expedient
 * @param {string} expedientId - Expedient ID
 * @returns {Promise<Array>} Email interactions
 */
export const getExpedientEmails = async expedientId => {
  try {
    const response = await axios.get(`${baseAPI}/expedients/${expedientId}/emails`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error getting expedient emails:', error);
    throw error;
  }
};

/**
 * Con qué identidad sale el correo de este expediente, y si puede salir.
 *
 * Se consulta ANTES de escribir, no al enviar: en AR el envío puede estar
 * bloqueado —sin dominio verificado, pausado por rebotes o sin cupo del día— y
 * descubrirlo después de redactar un correo largo es tirar el trabajo.
 *
 * @param {string} expedientId
 * @returns {Promise<{provider, from, reply_to, can_send, reason, message, capacity}>}
 */
export const getEmailSender = async expedientId => {
  try {
    const response = await axios.get(`${baseAPI}/expedients/${expedientId}/email-sender`, {
      headers: getAuthHeaders(),
    });
    return response.data.data;
  } catch (error) {
    console.error('Error getting email sender:', error);
    throw error;
  }
};

/**
 * Send email for an expedient
 * @param {string} expedientId - Expedient ID
 * @param {Object} emailData - { subject, body, reply_to_thread_id?, attachments? }
 * @returns {Promise<Object>} Created interaction
 */
export const sendEmail = async (expedientId, emailData) => {
  try {
    const response = await axios.post(
      `${baseAPI}/expedients/${expedientId}/emails/send`,
      emailData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

/**
 * List unmatched emails (admin only)
 * @param {Object} params - { page, limit, status, search, startDate, endDate }
 * @returns {Promise<Object>} { emails, pagination }
 */
export const getUnmatchedEmails = async (params = {}) => {
  try {
    const response = await axios.get(`${baseAPI}/emails/unmatched`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error getting unmatched emails:', error);
    throw error;
  }
};

/**
 * Get count of pending unmatched emails
 * @returns {Promise<number>} Count
 */
export const getUnmatchedEmailsCount = async () => {
  try {
    const response = await axios.get(`${baseAPI}/emails/unmatched/count`, {
      headers: getAuthHeaders(),
    });
    return response.data.count;
  } catch (error) {
    console.error('Error getting unmatched emails count:', error);
    throw error;
  }
};

/**
 * Get single unmatched email details
 * @param {string} emailId - Unmatched email ID
 * @returns {Promise<Object>} Email details
 */
export const getUnmatchedEmail = async emailId => {
  try {
    const response = await axios.get(`${baseAPI}/emails/unmatched/${emailId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error getting unmatched email:', error);
    throw error;
  }
};

/**
 * Assign email to expedient (admin only)
 * @param {string} emailId - Unmatched email ID
 * @param {string} expedientId - Target expedient ID
 * @returns {Promise<Object>} Result with interaction
 */
export const assignEmailToExpedient = async (emailId, expedientId) => {
  try {
    const response = await axios.post(
      `${baseAPI}/emails/unmatched/${emailId}/assign`,
      { expedient_id: expedientId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error assigning email:', error);
    throw error;
  }
};

/**
 * Ignore unmatched email (admin only)
 * @param {string} emailId - Unmatched email ID
 * @returns {Promise<Object>} Result
 */
export const ignoreUnmatchedEmail = async emailId => {
  try {
    const response = await axios.post(
      `${baseAPI}/emails/unmatched/${emailId}/ignore`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error ignoring email:', error);
    throw error;
  }
};

/**
 * Unassign an email interaction (move back to unmatched)
 * @param {string} interactionId - Interaction ID
 * @returns {Promise<Object>} Result
 */
export const unassignEmailInteraction = async interactionId => {
  try {
    const response = await axios.post(
      `${baseAPI}/emails/interactions/${interactionId}/unassign`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error unassigning email:', error);
    throw error;
  }
};

/**
 * Get Gmail sync status (admin only)
 * @returns {Promise<Object>} Sync status
 */
export const getGmailSyncStatus = async () => {
  try {
    const response = await axios.get(`${baseAPI}/emails/sync/status`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error getting sync status:', error);
    throw error;
  }
};

/**
 * Setup/renew Gmail watch (admin only)
 * @returns {Promise<Object>} Result
 */
export const setupGmailWatch = async () => {
  try {
    const response = await axios.post(
      `${baseAPI}/emails/sync/watch`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    console.error('Error setting up Gmail watch:', error);
    throw error;
  }
};
